import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getAgencyOverview } from "@/lib/agency.functions";

const GOLD = "#C8A84B";

export const Route = createFileRoute("/agency/")({
  component: AgencyOverviewPage,
});

function AgencyOverviewPage() {
  const fetchOverview = useServerFn(getAgencyOverview);
  const { data, isLoading } = useQuery({
    queryKey: ["agency-overview"],
    queryFn: () => fetchOverview(),
    staleTime: 30_000,
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  const balance = data?.balance ?? 0;
  const sub = data?.subscription;
  const audits = data?.recentAudits ?? [];
  const hasProfile = !!data?.profile && (data?.profile.brand_name ?? "").length > 0;

  return (
    <div className="grid gap-8">
      <header>
        <p
          className="text-[10px] font-medium uppercase tracking-[0.22em]"
          style={{ color: GOLD }}
        >
          Agency dashboard
        </p>
        <h1 className="mt-2 font-display text-3xl font-semibold text-foreground">
          Welcome back
        </h1>
      </header>

      <section className="grid gap-4 sm:grid-cols-3">
        <Card label="Credits available" value={String(balance)} />
        <Card
          label="Plan"
          value={
            sub
              ? sub.kind === "monthly"
                ? "Monthly subscription"
                : "One-time bundle"
              : ", "
          }
          sub={
            sub?.kind === "monthly" && sub.current_period_end
              ? `Renews ${new Date(sub.current_period_end).toLocaleDateString()}`
              : sub?.kind === "one_time"
                ? "Credits valid 12 months"
                : undefined
          }
        />
        <Card
          label="Status"
          value={sub?.status ? sub.status.replace(/_/g, " ") : "no plan"}
        />
      </section>

      <section className="grid gap-3 rounded-2xl border border-border bg-card/30 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-xl font-semibold text-foreground">
              Launch a new audit
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {balance > 0
                ? `Uses 1 of your ${balance} credits.`
                : "No credits, buy a bundle below."}
            </p>
          </div>
          {balance > 0 ? (
            <Link
              to="/agency/new-audit"
              className="rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background hover:opacity-90"
            >
              New audit →
            </Link>
          ) : (
            <Link
              to="/agency-bundle"
              className="rounded-full px-5 py-2.5 text-sm font-medium text-background hover:opacity-90"
              style={{ background: GOLD, color: "#0a0a0a" }}
            >
              Buy credits →
            </Link>
          )}
        </div>
        {!hasProfile && (
          <p className="rounded-lg border border-border bg-background/40 px-3 py-2 text-xs text-muted-foreground">
            Tip: set up your{" "}
            <Link to="/agency/brand" className="underline text-foreground">
              white-label brand
            </Link>{" "}
            first so reports go out under your name.
          </p>
        )}
      </section>

      <section>
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-xl font-semibold text-foreground">
            Recent audits
          </h2>
          <Link
            to="/agency/audits"
            className="text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground"
          >
            View all →
          </Link>
        </div>
        <div className="mt-4 grid gap-3">
          {audits.length === 0 && (
            <p className="rounded-2xl border border-border bg-card/20 p-6 text-sm text-muted-foreground">
              No audits launched yet.
            </p>
          )}
          {audits.map((a) => (
            <div
              key={a.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card/30 p-4 text-sm"
            >
              <div>
                <p className="font-medium text-foreground">
                  {a.client_name ?? a.website_url}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {a.order_number} · {a.website_url}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="rounded-full border border-border bg-background/40 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {a.status.replace(/_/g, " ")}
                </span>
                {a.report_url && (
                  <a
                    href={a.report_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-medium text-foreground underline"
                  >
                    Report
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Card({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card/30 p-5">
      <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-2 font-display text-2xl font-semibold text-foreground">
        {value}
      </p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}